/*
 * Every modal in the application (Radix `Dialog`): focus trap, Escape, the scroll lock and the
 * labelling are the primitive's, so no surface has to get them right again. A dialog always has a
 * title — it is what a screen reader announces on open — and the description is optional but
 * strongly encouraged for a confirm.
 *
 * Three slots, top to bottom: the **head** (title, description, the close button), the **body**
 * (`children`) and the **actions** (the footer's buttons: *Cancel* as `neutral`, the confirm as
 * `primary` or `danger`). The body is the only part that scrolls — a dialog taller than the window
 * keeps its title and its buttons in view and scrolls the list between them, which is what the
 * forward dialog and *Browse channels* need. `sheet` anchors the dialog to the end edge at full
 * height: the details column when the window is too narrow to keep it beside the conversation.
 *
 * `footer` is the actions slot under its older name. Callers still pass a `.tb-dialog__actions`
 * of their own inside it; the stylesheet flattens that nesting, and a caller that moves to
 * `actions` drops the wrapper.
 *
 * The way back out is this file's, not the primitive's. Radix restores the focus to its own
 * `Dialog.Trigger`, and no dialog in this application has one — every one of them is opened by a
 * control that sets `open` — so on close the focus fell to `<body>` and a keyboard reader started
 * again at the top of the document. The dialog therefore remembers the element that had the focus
 * when it opened and gives it back itself (UX §2.3, WCAG 2.4.3).
 */

import * as RadixDialog from "@radix-ui/react-dialog"
import { useLayoutEffect, useRef, type ReactNode } from "react"
import { t } from "../core/i18n/index.ts"
import { Icon } from "../core/shell/icons.tsx"
import { MAIN_ID } from "../core/shell/layout.ts"
import { cx } from "../lib/cx.ts"
import { Button } from "./Button.tsx"
import "./ui.css"

export interface DialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** Already translated; rendered as the accessible name of the dialog. */
	title: ReactNode
	description?: ReactNode
	/** Anchored to the end edge at full height instead of centred: the details column as a panel. */
	sheet?: boolean
	/**
	 * A dialog that is not a panel. `lightbox` is the picture viewer: the overlay carries the whole
	 * ground and the dialog has none of its own, because what is being looked at is a picture and a
	 * frame around it is furniture nobody asked for. The variant is two class names and nothing more —
	 * the rules belong to the viewer's own stylesheet (`modules/chat/attachments/attachments.css`), so
	 * the control layer keeps one dialog and one overlay.
	 */
	variant?: "lightbox"
	/** The body — the only slot that scrolls. A confirm whose whole text is its description has none. */
	children?: ReactNode
	/** The footer's buttons, drawn in one row at the end edge above a rule. */
	actions?: ReactNode
	/** The actions slot under its older name. */
	footer?: ReactNode
	/**
	 * Where the focus goes on close when the control that opened the dialog is gone — a choice inside
	 * it may unmount its own opener (a Reset button the reset hides). Absent, or answering null, the
	 * main region takes it.
	 */
	fallbackFocus?: () => HTMLElement | null
}

export function Dialog({
	open,
	onOpenChange,
	title,
	description,
	sheet = false,
	variant,
	children,
	actions,
	footer,
	fallbackFocus,
}: DialogProps) {
	// Read in a layout effect, which runs before the content's own effects move the focus into the
	// dialog: what is recorded here is the control that opened it. `<body>` is not a control — it is
	// what the document falls back to when the opener was unmounted in the same render (a menu item,
	// or a button the open state itself replaces) — so it is recorded as "no opener".
	const opener = useRef<HTMLElement | null>(null)
	useLayoutEffect(() => {
		if (!open) return
		const active = document.activeElement
		opener.current = active instanceof HTMLElement && active !== document.body ? active : null
	}, [open])

	const foot = actions ?? footer

	return (
		<RadixDialog.Root open={open} onOpenChange={onOpenChange}>
			<RadixDialog.Portal>
				<RadixDialog.Overlay
					className={cx("tb-overlay", variant !== undefined && `tb-overlay--${variant}`)}
				/>
				<RadixDialog.Content
					className={cx(
						"tb-elevated tb-elevated--page tb-dialog",
						sheet && "tb-dialog--sheet",
						variant !== undefined && `tb-dialog--${variant}`,
					)}
					onCloseAutoFocus={(event) => {
						// The opener if it is still in the document, else the main region — never
						// `<body>`, which is where a reader loses their place (UX §2.3). An opener
						// that is gone is the ordinary case of a dialog chosen from a menu: the
						// menu item was unmounted with the menu that carried it.
						const back = opener.current
						opener.current = null
						// Whoever has the focus by now keeps it. A choice made inside the dialog may
						// have opened the editor, a second dialog or a popover, and a surface that is
						// given the focus and has it taken away again reads that as the focus leaving
						// it — a popover dismisses itself on exactly that (ui/Menu closes the same
						// trap with `inert`).
						const active = document.activeElement
						if (active !== null && active !== document.body && active.isConnected) return
						const target =
							back !== null && back.isConnected
								? back
								: (fallbackFocus?.() ?? document.getElementById(MAIN_ID))
						if (target === null) return
						event.preventDefault()
						target.focus()
					}}
				>
					<div className="tb-dialog__head">
						<RadixDialog.Title className="tb-dialog__title">{title}</RadixDialog.Title>
						{description === undefined || description === null || description === "" ? null : (
							<RadixDialog.Description className="tb-dialog__description">
								{description}
							</RadixDialog.Description>
						)}
					</div>
					{children === undefined || children === null ? null : (
						<div className="tb-dialog__body">{children}</div>
					)}
					{foot === undefined || foot === null ? null : (
						<div className="tb-dialog__actions">{foot}</div>
					)}
					{/* Drawn over the head's corner but last in the document: the primitive's opening
					    focus goes to the first control, which must be the body's own field or button
					    and not the way out. The icon set rather than a glyph: every other close,
					    chevron and plus in the product is drawn on the same grid with the same stroke.
					    A viewer draws its own close in its own bar, and a second *Close* in the
					    accessible tree would be a way out nobody can see. */}
					{variant === "lightbox" ? null : (
						<RadixDialog.Close asChild>
							<Button
								className="tb-dialog__close"
								variant="ghost"
								quiet
								icon
								aria-label={t("action.close")}
								before={<Icon name="close" />}
							/>
						</RadixDialog.Close>
					)}
				</RadixDialog.Content>
			</RadixDialog.Portal>
		</RadixDialog.Root>
	)
}
